import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { debounce, isEqual } from 'lodash'
import { isAxiosError } from 'axios'
import { layoutStore, type PageLayoutStore } from 'shared/layout'
import { TableStore } from 'shared/ui/TableNew'
import { type IAlert } from 'shared/ui/Alert/types'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsApi } from 'entities/Contacts'
import { Contact } from 'entities/Contacts/model/Contact'
import { contactTemplate } from 'entities/Contacts/templates/contactTemplate'
import type { IParamsContacts, IResponseContact } from 'entities/Contacts/api/types'
import type { IResponseFilterSegment } from 'entities/Segment'
import { type Segment } from 'entities/Segment/model/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { RowContact } from 'entities/Contacts/model/Table/RowContact'
import {
  contactsDetailsGlobalStore,
  type ContactsDetailsManageStore,
} from 'widgets/ContactsDetails/store'
import { CallModalStore } from 'widgets/CallModal'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import type { IFilterGroup } from 'widgets/FilterEditor/types/types'
import type { FiltersConfig } from 'widgets/FilterEditor/model'
import { SelectedModalStore } from 'widgets/SelectContactsModal'
import { ExportStore } from 'widgets/Export'
import { sortByMapping } from '../helpers/helpers'

type IContactsTableStoreProps = {
  onChangeNoEmptyFilters: (value: IResponseFilterSegment[]) => void
  onSearch: (value: string) => void
  onTableWidth: (value: number) => void
  onChangeAlert: (item: IAlert | null) => void
}

const defaultSortBy = 'created_at'

export class ContactsTableStore {
  private _contactsMap: Map<number, Contact> = new Map()
  private _tableWidth: null | number = null
  private _filtersLoading = false
  private _totalAmount = 0 // without filters and search
  private _search = ''

  private _sortDisabled = true
  private _sortFieldType = ''
  private _isActiveDebounce = false
  private _activeItem: Contact | null = null
  private _inActionContactId: number | null = null

  // nextPage, privPages, useNextPage are using for hubspot only
  private _nextPage = 1
  private _privPages: number[] = []
  private _useNextPage = true

  private _newContactMode = false
  private _paramsFiltersList: IResponseFilterSegment[] = []

  private _disposeFilters: IReactionDisposer | null = null
  private _disposeLoadContacts: IReactionDisposer | null = null
  private _disposeTotalContactsByTrial: IReactionDisposer | null = null
  private _disposeOnSelectContacts: IReactionDisposer | null = null
  private _disposeNoEmptyFilters: IReactionDisposer | null = null
  private _disposeSearch: IReactionDisposer | null = null
  private _disposeTableWidth: IReactionDisposer | null = null

  private readonly _callModalStore: CallModalStore
  private _alert: IAlert | null = null
  private _firstEditMode = false
  private _cancelContactsListRequest = new RequestCancelAxios()
  private readonly _contactsTagsModalStore: ContactsTagsModalStore
  private _selectedModalStore = new SelectedModalStore()
  private _exportStore = new ExportStore()

  public debounceLoadContacts: ReturnType<typeof debounce>
  public cancelDebounceLoadContacts: () => void
  private _config: IContactsTableStoreProps | null = null
  private _segment: Segment | null = null
  private _segmentFilter: IResponseFilterSegment[] = []
  private _filterGroups: IFilterGroup[] = []
  private _filterGroupsOrigin: IFilterGroup[] = []
  private _filterConfig: FiltersConfig | null = null
  private readonly _tableStore: TableStore<RowContact>

  constructor(
    private _pageLayoutStore: PageLayoutStore,
    private _contactsDetailsManageStore: ContactsDetailsManageStore,
    private _entities: IEntitiesStore
  ) {
    this._tableStore = new TableStore({
      key: 'contacts',
      element: 'contact',
      filterRowsFn: (contact) => !contact.isDisabled && !contact.isNew,
      sortOrder: 'desc',
      getLimit: () => tableLimitStore.getLimit('contacts-table'),
      setLimit: (value) => tableLimitStore.setLimit('contacts-table', value),
      sortBy: 'created_at',
      onSort: (field) => {
        this.setSortFieldType(this.filterConfig?.getAnyFilter(field)?.type || '')
        this.loadContacts()
      },
      limit: 10,
    })

    this._tableStore.setBulkTotal(this.totalContactsByTrial)

    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._callModalStore = new CallModalStore(this._entities)
    this.debounceLoadContacts = debounce(this.loadContacts, 500)
    this.cancelDebounceLoadContacts = this.debounceLoadContacts.cancel

    makeAutoObservable(this)
  }

  setConfig = (config: IContactsTableStoreProps) => {
    this._config = config
  }

  setSegment = (segment: Segment | null) => {
    this._segment = segment
  }

  setSegmentFilter = (value: IResponseFilterSegment[]) => {
    this._segmentFilter = value
  }

  setFilterGroups = (value: IFilterGroup[]) => {
    this._filterGroups = value
  }

  setFilterGroupsOrigin = (value: IFilterGroup[]) => {
    this._filterGroupsOrigin = value
  }

  setFilterConfig = (value: FiltersConfig) => {
    this._filterConfig = value
  }

  get entities() {
    return this._entities
  }

  get exportStore() {
    return this._exportStore
  }

  get filterGroups() {
    return this._filterGroups
  }

  get selectedModalStore() {
    return this._selectedModalStore
  }

  get filterGroupsOrigin() {
    return this._filterGroupsOrigin
  }

  get filterConfig() {
    return this._filterConfig
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          data,
          'contacts',
          false,
          (contact: Contact) => {
            this.updateContactLocally(contact)
          }
        )
      },
    }
  }

  initReactions = () => {
    this.reactionFilters()
    this.reactionLoadContacts()
    this.reactionTotalContactsByTrial()
    this.reactionOnSelectContacts()
    this.reactionNoEmptyFilters()
    this.reactionSearch()
    this.reactionTableWidth()
  }

  resetNewContactMode = () => {
    if (this._newContactMode) {
      this._newContactMode = false
      this.setActiveContact(null)
      this.deleteNewContactLocally()
      this.loadContacts()
    }
  }

  resetPagination = () => {
    this._tableStore.dispose()
  }

  resetSearch = () => {
    this._search = ''
  }

  resetSorting = () => {
    this._tableStore.setSortOrder('desc')
    this._tableStore.setSortBy('created_at')
    this._sortDisabled = true
    this._sortFieldType = ''
  }

  setInActionId = (id: number | null) => {
    this._inActionContactId = id
  }

  changeSearchAndResetPaginationPage = (value: string) => {
    this._tableStore.page !== 1 && this.changePaginationToFirst()
    this.changeSearch(value)
    this.setActiveContact(null)
  }

  changeSearch = (value: string) => {
    this._isActiveDebounce = true
    this._search = value
    this.debounceLoadContacts?.()
  }

  setSortFieldType = (type: string) => {
    this._sortFieldType = type
  }

  handleChangePagination = (page: number, length: number) => {
    if (this._tableStore.bulkAllMode) {
      this._tableStore.resetSelected()
    }

    if (this.isHubspotWithValue) {
      const nextPageClick = page > this._tableStore.page
      this._useNextPage = nextPageClick

      if (!nextPageClick) {
        this._privPages.pop()
      }
      if (length !== this._tableStore.limit) {
        this._privPages = []
      }
    }

    this.changePagination(page, length)
  }

  changePagination = (page: number, length?: number) => {
    this._tableStore.setPage(page)

    if (length) {
      this._tableStore.setLimit(length)
    }
  }

  changePaginationToFirst = () => {
    this.changePagination(1)
  }

  handleTableWidth = (width: number | null) => {
    this._tableWidth = width
  }

  loadContactsList = (params?: IParamsContacts) => {
    this._cancelContactsListRequest.init()

    if (params?.search && this._entities.organizationStore.isHipaaCompliant) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.contact.searched',
        data: {
          search_query: params.search,
        },
      })
    }

    return ContactsApi.getContactsList(params, {
      cancelToken: this._cancelContactsListRequest.token,
    })
  }

  getTotalAmount = async () => {
    try {
      const { data } = await this.loadContactsList()

      this._totalAmount = data.total
    } catch (e) {
      console.log(e)
    }
  }

  checkIsTotalAmountSet = (total: number) => {
    if (
      this._entities.subscriptionStore.isTrial &&
      !this._search.length &&
      !this.noEmptyFilters.length
    ) {
      this._totalAmount = total
    }
  }

  handleCurrentNextPages = (currentPage: number, nextPage?: number) => {
    if (!this.isHubspotWithValue || !nextPage) return

    if (this._useNextPage) {
      this._privPages.push(currentPage)
    } else {
      this._privPages.pop()
    }
    this._nextPage = nextPage
  }

  setLoadingContacts = (value: boolean) => {
    this._tableStore.setLoading(value)
  }

  setLoading = (value: boolean) => {
    this._tableStore.setLoading(value)
  }

  loadContacts = async () => {
    let retrying = false

    try {
      runInAction(() => {
        this.setLoading(true)
      })

      this._tableStore.setBulkTotal(0)
      this._tableStore.bulkStore.resetBulkAll()

      const { data } = await this.loadContactsList(this.expandedParams)
      const results = data.data || []
      const total = data.total || 0

      this.handleCurrentNextPages(data.current_page, data.next_page)

      this._entities.contactsStore.reset([-1])
      this._entities.contactsStore.addItems(results)
      this.addContacts(results)
      this.checkIsTotalAmountSet(data.total)

      runInAction(() => {
        this._tableStore.setTotal(total)
        this._tableStore.setBulkTotal(this.totalContactsByTrial)
      })
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 408) {
        logger.warn(
          'TABLE: contacts-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
        )
        const currentLimit = this._tableStore.limit
        const newLimit = tableLimitStore.reduceLimit('contacts-table', currentLimit)
        this._tableStore.setLimit(newLimit)
        if (newLimit !== currentLimit) {
          retrying = true
          this.loadContacts()
        }
      } else {
        logger.error(e)
      }
    } finally {
      if (!retrying) {
        runInAction(() => {
          this._isActiveDebounce = false
          this.setLoading(false)
        })
      }
    }
  }

  updateActiveFilters = async (fieldName: string) => {
    try {
      runInAction(() => {
        this._filtersLoading = true
      })

      await this._entities.usersStore.updateActiveFields(fieldName)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._filtersLoading = false
      })
    }
  }

  addContact = (contact: Contact) => {
    this._contactsMap.set(contact.id, contact)
  }

  addContacts = (contacts: IResponseContact[]) => {
    this.resetContacts()

    contacts.forEach((item) => {
      this.addContact(new Contact(item))
    })
  }

  deleteNewContactLocally = () => {
    this._contactsMap.delete(-1)
  }

  updateContactLocally = (contact: Contact) => {
    this._contactsMap.set(contact.id, contact)
  }

  setActiveContact = (value: Contact | null) => {
    this._activeItem = value
  }

  resetActiveAndCloseDetailsIsMobile = () => {
    if (layoutStore.isMediumView) {
      contactsDetailsGlobalStore.handleToggleCollapse()
      this.setActiveContact(null)
    }
  }

  setActiveEmptyContact = () => {
    const contact = this._entities.contactsStore.addItem(contactTemplate)

    if (contact) {
      this.setActiveContact(contact)
    }
  }

  resetContacts = () => {
    this._contactsMap.clear()
  }

  changeNewContactMode = (value: boolean) => {
    if (value) {
      this._tableStore.unselectAllIds()
    }

    this._newContactMode = value
  }

  addNewContactLocallyAfterCreate = async (contact: Contact, params: { isNew?: boolean } = {}) => {
    this.deleteNewContactLocally()
    this.changeNewContactMode(false)
    this.setActiveContact(contact)

    if (params.isNew) {
      contactsDetailsGlobalStore.openBlockView('contactDetails')
      this.setIsFirstEditMode(true)

      this.setAlert({
        type: 'success',
        desc: 'Contact created',
      })

      this.loadContacts()
    } else {
      const isContactOnCurrentPageExist = this.getContactsList.find(
        (item) => item.id === contact.id
      )

      const list = isContactOnCurrentPageExist
        ? this.getContactsList.filter((item) => item.id !== contact.id)
        : this.getContactsList.slice(0, this.getContactsList.length - 1)

      const actualContacts = [contact, ...list]
      this.resetContacts()

      actualContacts.forEach((item) => {
        this.addContact(item)
      })
    }
  }

  addNewContactManually = () => {
    this.changeNewContactMode(true)
    this.setActiveEmptyContact()
  }

  getContactsFromIdArray = (ids: number[]) => {
    return this.getContactsList.filter((contact) => ids.includes(contact.id))
  }

  getContactById = (id: number) => {
    return this._contactsMap.get(id)
  }

  checkClosePanelAfterDelete = () => {
    if (this._activeItem && this._tableStore.selectedIds.includes(this._activeItem.id)) {
      this.setActiveContact(null)
    }
  }

  resetHubspotPagination = () => {
    this._privPages = []
    this._nextPage = 1
  }

  get mappedSortBy() {
    return sortByMapping[this._tableStore.sortBy]
  }

  get sortedBy() {
    const mappedSortBy = this._tableStore.sortBy.includes('custom.')
      ? this._tableStore.sortBy
      : this.mappedSortBy

    if (!mappedSortBy) {
      return [defaultSortBy]
    }

    if (this._sortDisabled && !this.hasHubspotListsFilter) {
      return ['disabled', mappedSortBy]
    }

    return [mappedSortBy]
  }

  get sortType() {
    const geoTypes = ['state_code', 'area_code', 'country', 'time_zone']

    if (geoTypes.includes(this._tableStore.sortBy)) {
      return {
        sortType: 'geo',
      }
    }

    return this._tableStore.sortBy.includes('custom.')
      ? { sortType: 'custom', fieldType: this._sortFieldType }
      : {}
  }

  get isHubspotWithValue() {
    if (!this._segment) return false

    return (
      this._segment.filtersList[0]?.filters[0]?.key === 'hubspot' &&
      this._segment.filtersList[0]?.filters[0].value
    )
  }

  get hasHubspotListsFilter() {
    return !!this._paramsFiltersList.reduce((count, item) => {
      if (!item.integration_lists) return 0

      const total = item.integration_lists.reduce((count, item) => {
        if (item.integration_key === 'hubspot' || item.integration_key === 'hubspot-lists')
          return count + 1
        return count
      }, 0)
      return count + total
    }, 0)
  }

  setParamsFiltersList = (value?: IResponseFilterSegment[]) => {
    this._paramsFiltersList = value || []
  }

  get paramsFiltersList() {
    return this._paramsFiltersList
  }

  get noEmptyFilters() {
    return this._paramsFiltersList || []
  }

  get actualPaginationData() {
    const pagination = {
      page: this._tableStore.page,
      length: this._tableStore.limit,
    }

    if (this.isHubspotWithValue) {
      pagination.page = this._useNextPage ? this._nextPage : this._privPages.at(-1) || 1
    }

    return pagination
  }

  get expandedParams(): IParamsContacts {
    return {
      ...this.actualPaginationData,
      ...this._tableStore.params,
      search: this.searchForParams,
      filtersList: this.noEmptyFilters,
      sortBy: this.sortedBy,
      ...this.sortType,
    }
  }

  get inActionId() {
    return this._inActionContactId
  }

  get selectedContacts() {
    return this.getContactsFromIdArray(this._tableStore.selectedIds as number[])
  }

  get getContactsList() {
    return Array.from(this._contactsMap.values())
  }

  get contacts() {
    const contactsList = this.getContactsList

    if (!this._newContactMode) {
      return contactsList
    }

    return [new Contact(contactTemplate), ...contactsList]
  }

  get contactsRows() {
    return this.contacts.map((item) => new RowContact(item))
  }

  get totalContactsByTrial() {
    const { isTrial, isPAYGPlan, trialActiveContactsCount } = this._entities.subscriptionStore

    if ((isTrial || isPAYGPlan) && this.total > trialActiveContactsCount) {
      return trialActiveContactsCount
    }

    return this.total
  }

  get isLoading() {
    return this._filtersLoading || this._tableStore.loading || this._isActiveDebounce
  }

  get activeContact() {
    return this._activeItem
  }

  get isNewContactMode() {
    return this._newContactMode
  }

  get isEmptyContactsList() {
    return !this.contacts.length && !this.isLoading && !this.isNewContactMode
  }

  get isNoContactYet() {
    return this.isEmptyContactsList && !this._search.length
  }

  reactionFilters = () => {
    this._disposeFilters?.()
    this._disposeFilters = reaction(
      () => this._segmentFilter,
      (segmentFilter) => {
        const is_equal = isEqual(this.expandedParams.filtersList, segmentFilter)

        if (is_equal) return

        this.resetHubspotPagination()
        this.changePaginationToFirst()
        this.changeSearchAndResetPaginationPage('')
        this.setParamsFiltersList(segmentFilter)
        this.debounceLoadContacts?.()
      }
    )
  }

  reactionTotalContactsByTrial = () => {
    this._disposeLoadContacts?.()
    this._disposeLoadContacts = reaction(
      () => this.totalContactsByTrial,
      this._tableStore.setBulkTotal
    )
  }

  reactionLoadContacts = () => {
    this._disposeTotalContactsByTrial?.()
    this._disposeTotalContactsByTrial = reaction(
      () => this._tableStore.loading,
      (isLoading) => {
        if (!this._entities.subscriptionStore.isTrial || !isLoading) return

        if (this._search.length || this.noEmptyFilters.length) {
          this.getTotalAmount()
        }
      }
    )
  }

  reactionOnSelectContacts = () => {
    this._disposeOnSelectContacts?.()
    this._disposeOnSelectContacts = reaction(
      () => this._tableStore.selectedIds,
      (ids) => {
        if (ids.length) {
          this.resetNewContactMode()
        }
      }
    )
  }

  reactionNoEmptyFilters = () => {
    this._disposeNoEmptyFilters?.()
    this._disposeNoEmptyFilters = reaction(
      () => this.noEmptyFilters,
      (value) => {
        this._config?.onChangeNoEmptyFilters(value)
      }
    )
  }

  reactionSearch = () => {
    this._disposeSearch?.()
    this._disposeSearch = reaction(
      () => this._search,
      (value) => {
        this._config?.onSearch(value)
      }
    )
  }

  reactionTableWidth = () => {
    this._disposeTableWidth?.()
    this._disposeTableWidth = reaction(
      () => this._tableWidth,
      (value) => {
        if (value) {
          this._config?.onTableWidth(value)
        }
      }
    )
  }

  dispose = () => {
    this._disposeFilters?.()
    this._disposeLoadContacts?.()
    this._disposeTotalContactsByTrial?.()
    this._disposeOnSelectContacts?.()
    this._disposeNoEmptyFilters?.()
    this._disposeSearch?.()
    this._disposeTableWidth?.()
    this._cancelContactsListRequest.reset()
    this.cancelDebounceLoadContacts()
    this._paramsFiltersList = []
    this._newContactMode = false
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get callModalStore() {
    return this._callModalStore
  }

  get tableStore() {
    return this._tableStore
  }

  get alert() {
    return this._alert
  }

  setAlert = (item: IAlert) => {
    this._alert = item
    this._config?.onChangeAlert(item)
  }

  resetAlert = () => {
    this._alert = null
    this._config?.onChangeAlert(null)
  }

  setIsFirstEditMode = (condition: boolean) => {
    this._firstEditMode = condition
  }

  get isFirstEditMode() {
    return this._firstEditMode
  }

  get search() {
    return this._search
  }

  get searchForParams() {
    return removeSpacesFromNumericString(this._search)
  }

  get tableWidth() {
    return this._tableWidth
  }

  get totalAmount() {
    return this._totalAmount
  }

  get total() {
    return this._tableStore.total
  }

  get segment() {
    return this._segment
  }

  get title() {
    if (!this._segment || this._segment.id === -1) return 'All contacts'

    return this._segment.name
  }
}
